import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import { useApi } from "@/core/api";
import { enqueueSheetLayoutMutation } from "@/payroll/ai-script/sheet-layout-mutation-queue";
import { patchSpreadsheetSheetLayout } from "@/payroll/ai-script/spreadsheet-cache";
import {
  aiScriptSpreadsheetSheetKey,
  aiScriptSpreadsheetSheetQueryOptions,
  getAiScriptSpreadsheetSheetPath,
} from "@/payroll/ai-script/spreadsheet-sheet-query";
import type {
  AiSpreadsheetValueType,
  IAiSpreadsheetSheetResponse,
  IUpdateAiSpreadsheetSheetResponse,
} from "@/payroll/ai-script/types";
import { workbookMutationRequestConfig } from "@/payroll/ai-script/workbook-mutation";
import type { WorkbookReadSource } from "@/payroll/ai-script/workbook-read-source";
import { executeWorkbookMutation } from "./workbook-refresh";

interface UpdateAiSpreadsheetSheetBaseParams {
  runId: string;
  sheetId: string;
  source?: WorkbookReadSource;
}

export type UpdateAiSpreadsheetSheetParams =
  UpdateAiSpreadsheetSheetBaseParams &
    (
      | { columnWidths: Record<string, number> }
      | { rowHeights: Record<string, number> }
      | { frozenColumn: number | null }
      | { frozenRow: number | null }
    ) & {
      columnWidths?: Record<string, number>;
      rowHeights?: Record<string, number>;
      frozenColumn?: number | null;
      frozenRow?: number | null;
    };

const SHEET_LAYOUT_REQUEST_TIMEOUT_MS = 30_000;

export const useUpdateAiSpreadsheetColumnValueType = (
  businessId: string | null,
) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: {
      runId: string;
      sheetId: string;
      col: number;
      valueType: AiSpreadsheetValueType;
      baseVersion?: number;
    }) => {
      if (!businessId) throw new Error("Business is required");
      return executeWorkbookMutation({
        api,
        queryClient,
        businessId,
        runId: params.runId,
        immediateSheetId: params.sheetId,
        submit: (idempotencyKey) =>
          api
            .patch(
              `${getAiScriptSpreadsheetSheetPath(businessId, params.runId, params.sheetId)}/columns/${params.col}`,
              {
                value_type: params.valueType,
                ...(params.baseVersion != null
                  ? { base_version: params.baseVersion }
                  : {}),
              },
              workbookMutationRequestConfig(idempotencyKey),
            )
            .then((res) => res.data),
      });
    },
    onSuccess: (result, params) => {
      for (const sheetId of result.changed_sheet_ids ?? []) {
        void queryClient.prefetchQuery(
          aiScriptSpreadsheetSheetQueryOptions(
            api,
            businessId,
            params.runId,
            sheetId,
            undefined,
            queryClient,
          ),
        );
      }
    },
  });
};

export const patchAiSpreadsheetSheetLayout = (
  api: AxiosInstance,
  businessId: string,
  params: UpdateAiSpreadsheetSheetParams,
) => {
  const body = {
    ...(params.columnWidths !== undefined && {
      column_widths: params.columnWidths,
    }),
    ...(params.rowHeights !== undefined && { row_heights: params.rowHeights }),
    ...(params.frozenRow !== undefined && { frozen_row: params.frozenRow }),
    ...(params.frozenColumn !== undefined && {
      frozen_column: params.frozenColumn,
    }),
  };

  return api
    .patch(
      getAiScriptSpreadsheetSheetPath(
        businessId,
        params.runId,
        params.sheetId,
        params.source,
      ),
      body,
      { timeout: SHEET_LAYOUT_REQUEST_TIMEOUT_MS },
    )
    .then((res) => res.data as IUpdateAiSpreadsheetSheetResponse);
};

export const useUpdateAiSpreadsheetSheet = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: UpdateAiSpreadsheetSheetParams) => {
      if (!businessId) throw new Error("Business is required");

      return enqueueSheetLayoutMutation({
        businessId,
        runId: params.runId,
        sheetId: params.sheetId,
        execute: () => patchAiSpreadsheetSheetLayout(api, businessId, params),
      });
    },
    onSuccess: (response, { runId, sheetId, source }) => {
      queryClient.setQueryData<IAiSpreadsheetSheetResponse>(
        aiScriptSpreadsheetSheetKey(businessId, runId, sheetId, source),
        (data) => patchSpreadsheetSheetLayout(data, response.sheet),
      );
    },
  });
};
